<template>
    <div :class="['container container--sm', $style.timeline]" ref="wrapper">
      <component
        v-for="({ label, value, to }) of lines"
        :is="to ? link : div"
        :to="localePath(to)"
        :key="value"
        :class="[$style.item, modelValue === value && $style.active, 'item']"
        @click="(e) => updateValue(e, value) && !to"
      >
        {{ label }}
      </component>

      <client-only>
        <div
          :class="$style.activeLine"
          :style="{
            width: `${tabItemWidth}px`,
            transform: `translateX(${tabItemPositionleft}px)`,
          }"
        />
      </client-only>
      <div :class="$style.bottomLine" />
    </div>
</template>

<script setup lang="ts">
import { type TimeLineParams } from './index';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const localePath = useLocalePath();

const props = defineProps<TimeLineParams>();
const emits = defineEmits(['update:modelValue']);

const wrapper = ref<HTMLElement>();

const link = resolveComponent('nuxt-link');
const div = resolveComponent('div');

const isMobile = computed<boolean>(() => window?.innerWidth < 768 || false);

const tabItemWidth = ref();
const tabItemPositionleft = ref();


const updateValue = (e: eventTarget, value: string): void => {
  tabItemWidth.value = e.target.offsetWidth;
  tabItemPositionleft.value = e.target.offsetLeft;
  emits('update:modelValue', value);
};

const { $localePath } = useNuxtApp();

onMounted(() => {
  const activeIndex = props.lines.findIndex((line) => {
    if (line.to) {
      return $localePath(line.to) === props.modelValue;
    }

    return line.value === props.modelValue;
  });

  const activeItem = wrapper.value.querySelectorAll('.item')[activeIndex];
  if (activeItem) {
    tabItemWidth.value = activeItem.offsetWidth;
    tabItemPositionleft.value = activeItem.offsetLeft;
  }
});
</script>

<style lang="scss" module>
  .timeline {
    position: relative;
    display: flex;
    padding-left: 30px;
    padding-right: 30px;
    margin-bottom: 50px;

    @media (max-width: 767px) {
      max-width: unset;
      width: fit-content;
      margin-bottom: 0;
      padding: 0;
    }
  }

  .item {
    text-align: center;
    padding: 12px 0;
    text-decoration: none;
    color: #596269;
    transition: 200ms ease;
    cursor: pointer;

    @media (min-width: 768px) {
      flex: 1;
    }

    @media (max-width: 767px) {
      flex-shrink: 0;
      min-width: 148px;
      padding: 12px;
      white-space: nowrap;
      font-size: 12px;
    }
  }

  .active {
    color: #121E29;
  }

  .bottomLine {
    position: absolute;
    bottom: 0;
    left: 30px;
    right: 30px;
    height: 4px;
    border-radius: 2px;
    background: #D9D9D9;
    user-select: none;
    pointer-events: none;

    @media (max-width: 1023px) {
      width: 100%;
      right: unset;
      left: 0;
    }
  }

  .activeLine {
    z-index: 2;
    position: absolute;
    left: 30px;
    bottom: 0;
    height: 4px;
    border-radius: 2px;
    background-color: #293EFF;
    user-select: none;
    pointer-events: none;
    transition: 400ms ease;
    transform-origin: left;

    @media (min-width: 768px) {
      margin-left: -30px;
    }

    @media (max-width: 767px) {
      left: 0;
    }
  }
</style>